import DataApi from '../../api/DataApi';
import {
  normalizePropertyRecords,
  buildFloorVisualization,
  buildPortfolioStats,
  buildPropertyStats,
  buildUnitDrillDownRows,
  filterUnitDrillDownRows,
  getDrillDownLabel,
  getRecordFieldValue,
  recordId,
} from './propertyRecordUtils';

const OBJECT_NAME = 'property';
export const PORTFOLIO_TAB_ID = '__portfolio__';

const PROPERTY_FIELDS = [
  'id',
  'name',
  'property_code',
  'property_type',
  'ownership_type',
  'address',
  'city',
  'state',
  'country',
  'postal_code',
  'total_floors',
  'total_units',
  'status',
  'description',
].join(',');

const PROPERTY_SEARCH_FIELDS = ['name', 'property_code', 'city'];

const DEFAULT_LIMIT = 10;

function buildActivePropertyFilters(propertyType) {
  const filters = [{ field: 'status', operator: 'equals', value: 'Active' }];
  if (propertyType && propertyType !== 'All') {
    filters.push({
      field: 'property_type',
      operator: 'equals',
      value: propertyType,
    });
  }
  return filters;
}

/** Same filter query shape as SearchViewLayout → GET /api/object/list/:object */
function appendListFilters(params, filters) {
  filters.forEach((filter, index) => {
    if (filter?.field && filter?.operator != null) {
      params.append(
        `filter${index + 1}`,
        `${filter.field}:${filter.operator}:${filter.value ?? ''}`
      );
    }
  });
}

function buildFiltersAndLogic(q, propertyType) {
  const baseFilters = buildActivePropertyFilters(propertyType);
  const trimmedQ = String(q || '').trim();

  if (!trimmedQ) {
    return { filters: baseFilters, logic: 'AND' };
  }

  const searchFilters = PROPERTY_SEARCH_FIELDS.map(field => ({
    field,
    operator: 'contains',
    value: trimmedQ,
  }));

  const allFilters = [...baseFilters, ...searchFilters];
  const baseLen = baseFilters.length;
  const orPart = searchFilters.map((_, idx) => baseLen + idx + 1).join(' OR ');

  if (baseLen === 0) {
    return { filters: searchFilters, logic: 'OR' };
  }
  if (baseLen === 1) {
    return { filters: allFilters, logic: `1 AND (${orPart})` };
  }
  const andPart = baseFilters.map((_, idx) => idx + 1).join(' AND ');
  return { filters: allFilters, logic: `(${andPart}) AND (${orPart})` };
}

/**
 * Fetch properties via existing GET /api/object/list/property (ObjectModel.fetchRecords).
 */
export async function fetchPropertyList({
  q = '',
  propertyType = 'All',
  limit = DEFAULT_LIMIT,
  offset = 0,
  signal,
} = {}) {
  const { filters, logic } = buildFiltersAndLogic(q, propertyType);

  const params = new URLSearchParams();
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  params.append('sortBy', 'name');
  params.append('sortOrder', 'ASC');
  params.append('fields', PROPERTY_FIELDS);
  params.append('logic', logic);
  appendListFilters(params, filters);

  const api = new DataApi(`object/list/${OBJECT_NAME}`);
  const response = await api.fetchAll(params.toString(), { signal });
  const payload = response?.data;

  if (!payload?.success) {
    throw new Error(payload?.message || payload?.error || 'Failed to load properties');
  }

  return normalizePropertyRecords(payload.data);
}

async function fetchObjectList({
  objectName,
  fields,
  filters = [],
  logic = 'AND',
  sortBy,
  sortOrder = 'ASC',
  limit = 500,
  signal,
}) {
  const params = new URLSearchParams();
  params.append('limit', String(limit));
  if (fields) {
    params.append('fields', Array.isArray(fields) ? fields.join(',') : fields);
  }
  if (sortBy) {
    params.append('sortBy', sortBy);
    params.append('sortOrder', sortOrder);
  }
  if (filters.length > 0) {
    params.append('logic', logic);
    appendListFilters(params, filters);
  }

  const api = new DataApi(`object/list/${objectName}`);
  const response = await api.fetchAll(params.toString(), { signal });
  const payload = response?.data;
  if (!payload?.success) {
    throw new Error(payload?.message || payload?.error || `Failed to load ${objectName}`);
  }
  return Array.isArray(payload.data) ? payload.data : [];
}

/**
 * Floors + units + open interests for the vertical floor visualization.
 */
export async function fetchFloorVisualization(propertyId, signal) {
  if (!propertyId) {
    return { floors: [], stats: { available: 0, occupied: 0, highDemand: 0 } };
  }

  const propertyFilter = [
    { field: 'property_id', operator: 'equals', value: propertyId },
  ];

  const [floorRows, unitRows, interestRows] = await Promise.all([
    fetchObjectList({
      objectName: 'floor',
      fields: ['id', 'name', 'property_id', 'floor_number', 'total_units', 'status'],
      filters: propertyFilter,
      sortBy: 'floor_number',
      sortOrder: 'DESC',
      signal,
    }),
    fetchObjectList({
      objectName: 'unit',
      fields: [
        'id',
        'name',
        'unit_number',
        'property_id',
        'floor_id',
        'unit_type',
        'monthly_rent',
        'security_deposit',
        'area',
        'bedrooms',
        'bathrooms',
        'furnishing',
        'status',
      ],
      filters: propertyFilter,
      sortBy: 'unit_number',
      sortOrder: 'ASC',
      signal,
    }),
    fetchObjectList({
      objectName: 'property_interest',
      fields: ['id', 'name', 'lead_id', 'unit_id', 'property_id', 'status', 'priority', 'budget_min', 'budget_max'],
      filters: propertyFilter,
      signal,
    }),
  ]);

  return buildFloorVisualization({ floorRows, unitRows, interestRows });
}

/**
 * Portfolio-wide KPIs via parallel fetchRecords list calls (properties, units, interests).
 */
export async function fetchPortfolioStats({ propertyType = 'All', signal } = {}) {
  const propertyFilters = [{ field: 'status', operator: 'equals', value: 'Active' }];
  if (propertyType && propertyType !== 'All') {
    propertyFilters.push({
      field: 'property_type',
      operator: 'equals',
      value: propertyType,
    });
  }

  const [propertyRows, unitRows, interestRows] = await Promise.all([
    fetchObjectList({
      objectName: 'property',
      fields: ['id', 'name', 'status', 'property_type'],
      filters: propertyFilters,
      sortBy: 'name',
      sortOrder: 'ASC',
      limit: 500,
      signal,
    }),
    fetchObjectList({
      objectName: 'unit',
      fields: ['id', 'property_id', 'status'],
      limit: 500,
      signal,
    }),
    fetchObjectList({
      objectName: 'property_interest',
      fields: ['id', 'unit_id', 'property_id', 'status'],
      limit: 500,
      signal,
    }),
  ]);

  return buildPortfolioStats({ propertyRows, unitRows, interestRows });
}

async function fetchPortfolioScope(propertyType, propertyId, signal) {
  if (propertyId) {
    const propertyRows = await fetchObjectList({
      objectName: 'property',
      fields: ['id', 'name', 'status', 'property_type'],
      filters: [{ field: 'id', operator: 'equals', value: propertyId }],
      limit: 1,
      signal,
    });
    return { propertyRows, propertyIds: new Set([propertyId].filter(Boolean)) };
  }

  const propertyFilters = [{ field: 'status', operator: 'equals', value: 'Active' }];
  if (propertyType && propertyType !== 'All') {
    propertyFilters.push({
      field: 'property_type',
      operator: 'equals',
      value: propertyType,
    });
  }

  const propertyRows = await fetchObjectList({
    objectName: 'property',
    fields: ['id', 'name', 'status', 'property_type'],
    filters: propertyFilters,
    sortBy: 'name',
    sortOrder: 'ASC',
    limit: 500,
    signal,
  });
  const propertyIds = new Set(propertyRows.map(recordId).filter(Boolean));
  return { propertyRows, propertyIds };
}

const UNIT_DRILL_FIELDS = [
  'id',
  'name',
  'unit_number',
  'property_id',
  'unit_type',
  'monthly_rent',
  'status',
];

/**
 * Single-property inventory + demand stats for Detail tab rollup.
 */
export async function fetchPropertyDetailStats(propertyId, signal) {
  if (!propertyId) {
    return null;
  }

  const propertyFilter = [{ field: 'property_id', operator: 'equals', value: propertyId }];
  const [unitRows, interestRows] = await Promise.all([
    fetchObjectList({
      objectName: 'unit',
      fields: UNIT_DRILL_FIELDS,
      filters: propertyFilter,
      sortBy: 'unit_number',
      sortOrder: 'ASC',
      signal,
    }),
    fetchObjectList({
      objectName: 'property_interest',
      fields: ['id', 'unit_id', 'property_id', 'status'],
      filters: propertyFilter,
      signal,
    }),
  ]);

  return buildPropertyStats({ propertyId, unitRows, interestRows });
}

/**
 * Filtered unit rows for portfolio or property drill-down panels.
 */
export async function fetchDrillDownUnits({
  drillDown,
  propertyType = 'All',
  propertyId = null,
  propertyName = null,
  signal,
} = {}) {
  const { propertyRows, propertyIds } = await fetchPortfolioScope(
    propertyType,
    propertyId,
    signal
  );

  const unitFilters = propertyId
    ? [{ field: 'property_id', operator: 'equals', value: propertyId }]
    : [];

  const [unitRows, interestRows] = await Promise.all([
    fetchObjectList({
      objectName: 'unit',
      fields: UNIT_DRILL_FIELDS,
      filters: unitFilters,
      sortBy: 'unit_number',
      sortOrder: 'ASC',
      limit: 500,
      signal,
    }),
    fetchObjectList({
      objectName: 'property_interest',
      fields: ['id', 'unit_id', 'property_id', 'status'],
      limit: 500,
      signal,
    }),
  ]);

  const propertyNameById = {};
  propertyRows.forEach(row => {
    const id = recordId(row);
    if (id) {
      propertyNameById[id] = getRecordFieldValue(row, 'name') || 'Property';
    }
  });

  const allRows = buildUnitDrillDownRows({
    unitRows,
    interestRows,
    propertyIds,
    propertyNameById,
  });
  const units = filterUnitDrillDownRows(allRows, drillDown);
  const labels = getDrillDownLabel(drillDown, {
    propertyName: propertyName || propertyNameById[propertyId],
  });

  return { units, ...labels };
}

export { DEFAULT_LIMIT as PROPERTY_LIST_DEFAULT_LIMIT };
